import React, { useEffect, useRef, useState } from 'react';
import { sitePopup, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Save, RotateCcw, Upload, ExternalLink, Eye, EyeOff, Trash2 } from 'lucide-react';

const FREQ_OPTIONS = [
  { value: 'always', label: 'Every visit' },
  { value: 'session', label: 'Once per session' },
  { value: 'user', label: 'Once per user (permanent per account)' },
];

function ColorInput({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-color`}
          className="w-10 h-10 rounded border border-[#2a2a2a] bg-black cursor-pointer" />
        <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-text`}
          placeholder="#000000"
          className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-2 py-2 text-sm font-mono" />
      </div>
    </div>
  );
}

export default function AdminPopupPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setCfg(await sitePopup.get()); }
    catch (e) { toast({ title: 'Load failed', description: e?.response?.data?.detail || '' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (patch) => setCfg(prev => ({ ...prev, ...patch }));
  const updateBtn = (i, patch) => {
    const buttons = [...(cfg.buttons || [])];
    buttons[i] = { ...buttons[i], ...patch };
    setCfg(prev => ({ ...prev, buttons }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await sitePopup.save({
        enabled: cfg.enabled, headline: cfg.headline, body: cfg.body,
        bgColor: cfg.bgColor, textColor: cfg.textColor, imageUrl: cfg.imageUrl,
        buttons: cfg.buttons, frequency: cfg.frequency,
      });
      setCfg(saved);
      toast({ title: 'Pop-up saved', description: 'Live on the site immediately.' });
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' }); }
    finally { setSaving(false); }
  };

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await filesApi.upload(file, 'business');
      update({ imageUrl: r.url });
      toast({ title: 'Image uploaded', description: 'Click Save to publish it.' });
    } catch (e) { toast({ title: 'Upload failed', description: e?.response?.data?.detail || '' }); }
    finally { setUploading(false); }
  };

  const doReset = async () => {
    if (!window.confirm('Reset the pop-up to disabled defaults? Current headline/body/image will be cleared.')) return;
    setResetting(true);
    try {
      const r = await sitePopup.reset();
      setCfg(r.config);
      toast({ title: 'Pop-up reset', description: 'The pop-up is now hidden from the public site.' });
    } catch (e) { toast({ title: 'Reset failed', description: e?.response?.data?.detail || '' }); }
    finally { setResetting(false); }
  };

  if (loading || !cfg) {
    return <div className="pbb-card p-10 text-center"><Loader2 className="mx-auto animate-spin text-green-400" size={22}/></div>;
  }

  const bgPreview = cfg.bgColor || '#0a140d';
  const fgPreview = cfg.textColor || '#ffffff';
  const imgSrc = cfg.imageUrl?.startsWith('http') ? cfg.imageUrl : (cfg.imageUrl ? filesApi.urlFor(cfg.imageUrl) : '');

  return (
    <div className="space-y-4" data-testid="admin-popup-panel">
      {/* Header + toggle */}
      <div className="pbb-card p-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-green-300 font-semibold">Pop-up Manager</div>
          <h2 className="text-xl font-black text-white mt-1">Sitewide pop-up — live edit</h2>
          <p className="text-sm text-gray-400 mt-1">Toggle on/off, edit copy, swap the banner image, and set up to 3 buttons. Every change goes live immediately.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => update({ enabled: !cfg.enabled })}
            data-testid="popup-toggle-enabled"
            className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold ${cfg.enabled ? 'bg-green-500/15 text-green-300 border border-green-500/40' : 'bg-neutral-500/15 text-neutral-300 border border-neutral-500/40'}`}>
            {cfg.enabled ? <Eye size={14}/> : <EyeOff size={14}/>} {cfg.enabled ? 'On' : 'Off'}
          </button>
          <button type="button" onClick={doReset} disabled={resetting} data-testid="popup-reset"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm">
            {resetting ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>} Delete / Reset
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Content editors */}
        <div className="space-y-3 pbb-card p-4">
          <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Content</div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Headline</label>
            <input value={cfg.headline || ''} onChange={(e) => update({ headline: e.target.value })}
              data-testid="popup-headline"
              className="w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Body / description</label>
            <textarea rows={3} value={cfg.body || ''} onChange={(e) => update({ body: e.target.value })}
              data-testid="popup-body"
              className="w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <ColorInput label="Background color" value={cfg.bgColor} onChange={(v) => update({ bgColor: v })} testId="popup-bg"/>
            <ColorInput label="Text color" value={cfg.textColor} onChange={(v) => update({ textColor: v })} testId="popup-fg"/>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Banner image</label>
            <div className="flex items-center gap-2">
              <input value={cfg.imageUrl || ''} onChange={(e) => update({ imageUrl: e.target.value })}
                data-testid="popup-image-url"
                placeholder="https://... or upload below"
                className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-xs font-mono" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) doUpload(f); }} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="popup-image-upload"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#2a2a2a] text-gray-200 hover:border-green-500 text-xs disabled:opacity-60">
                {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Upload
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Show frequency</label>
            <select value={cfg.frequency || 'session'} onChange={(e) => update({ frequency: e.target.value })}
              data-testid="popup-frequency"
              className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-2 text-sm">
              {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Buttons + preview */}
        <div className="space-y-3">
          <div className="pbb-card p-4 space-y-3">
            <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Buttons (up to 3)</div>
            {(cfg.buttons || []).slice(0, 3).map((b, i) => (
              <div key={i} className="border border-[#1f1f1f] rounded-lg p-3 space-y-2 bg-[#0d1f14]/40">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 font-semibold">Button {i + 1}</span>
                  <label className="ml-auto inline-flex items-center gap-1 text-xs text-gray-300 cursor-pointer">
                    <input type="checkbox" checked={!!b.enabled} onChange={(e) => updateBtn(i, { enabled: e.target.checked })}
                      data-testid={`popup-btn-${i}-enabled`}
                      className="accent-green-600"/>
                    Show
                  </label>
                </div>
                <input value={b.label || ''} onChange={(e) => updateBtn(i, { label: e.target.value })}
                  placeholder="Button label" data-testid={`popup-btn-${i}-label`}
                  className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
                <input value={b.url || ''} onChange={(e) => updateBtn(i, { url: e.target.value })}
                  placeholder="URL — https://... or /internal/path"
                  data-testid={`popup-btn-${i}-url`}
                  className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-xs font-mono" />
                <label className="inline-flex items-center gap-1 text-xs text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={b.target === '_blank'}
                    onChange={(e) => updateBtn(i, { target: e.target.checked ? '_blank' : '_self' })}
                    data-testid={`popup-btn-${i}-newtab`}
                    className="accent-green-600"/>
                  Open in a new tab
                </label>
              </div>
            ))}
          </div>

          {/* Live preview */}
          <div className="pbb-card p-4 space-y-2">
            <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider flex items-center gap-2">
              Live preview <ExternalLink size={11}/>
            </div>
            <div className="rounded-xl overflow-hidden border border-[#1f1f1f]"
              style={{ background: bgPreview, color: fgPreview }} data-testid="popup-preview">
              {imgSrc && <img src={imgSrc} alt="" className="w-full h-auto block" />}
              {(cfg.headline || cfg.body) && (
                <div className="px-3 pt-3 pb-1 text-center">
                  {cfg.headline && <div className="font-black text-lg">{cfg.headline}</div>}
                  {cfg.body && <div className="text-xs opacity-90 mt-1">{cfg.body}</div>}
                </div>
              )}
              <div className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {(cfg.buttons || []).filter(b => b.enabled && b.label).map((b, i) => (
                  <div key={i} className="px-3 py-2 rounded-full bg-green-500 text-black font-semibold text-xs text-center truncate">{b.label}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 bg-[#050808]/95 backdrop-blur border-t border-[#1f1f1f] p-3 flex items-center justify-end gap-2 z-10">
        <button type="button" onClick={load}
          className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 text-sm inline-flex items-center gap-1">
          <RotateCcw size={14}/> Revert
        </button>
        <button type="button" onClick={save} disabled={saving} data-testid="popup-save"
          className="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black font-semibold text-sm inline-flex items-center gap-1 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save & Go Live
        </button>
      </div>
    </div>
  );
}
